import { useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { useForm, UseFormReturn } from "react-hook-form";
import { SASS, SASS_TEMPLATE, STFF, STFF_TEMPLATE } from "@/models/stff.tsx";
import { SCLR, SCLR_TEMPLATE, GlobalStateModel } from "@/models";
import { useLeaData } from "@/queries.ts";
import { StaffSearchFormState } from "../StaffWizardSearch";

export const MAX_ASSIGNMENTS = 10;
export const MAX_CLASSROOMS = 10;

type LockedFields = {
  staffLocalId: boolean;
  firstName: boolean;
  lastName: boolean;
  dateOfBirth: boolean;
};

const UNSET_ETHNICITY = "__UNSET__";

const convertEthnicity = (value: any, missingIndicator: any): string => {
  if (value === true || value === "Y") return "Y";
  if (value === false || value === "N") return "N";
  if (missingIndicator === true || missingIndicator === "Y") return "";
  return UNSET_ETHNICITY;
};

const convertMissingIndicator = (value: any): string => {
  if (value === true || value === "Y") return "Y";
  if (value === false || value === "N") return "N";
  return "Y";
};

export function useAddStaffWizard() {
  const leaContext = useRouteContext({ from: "/$leaid" }) as GlobalStateModel;
  const result = useLeaData(leaContext.currentUserOrg?.esiIdPath ?? "");
  const leaData = result.data;

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([1]);
  const [searchResults, setSearchResults] = useState<STFF[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [searchedData, setSearchedData] = useState({ name: "" });
  const [searchCriteria, setSearchCriteria] =
    useState<StaffSearchFormState | null>(null);
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [lockedFields, setLockedFields] = useState<LockedFields>({
    staffLocalId: false,
    firstName: false,
    lastName: false,
    dateOfBirth: false,
  });
  const [searchError, setSearchError] = useState<string>("");

  const [skipAssignment, setSkipAssignment] = useState(false);
  const [skipClassroom, setSkipClassroom] = useState(false);

  const form = useForm<STFF>({
    defaultValues: {
      ...STFF_TEMPLATE,
      StaffEthnicity: UNSET_ETHNICITY,
    },
    mode: "onChange",
  });

  const assignmentForm = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm2 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm3 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm4 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm5 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm6 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm7 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm8 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm9 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });
  const assignmentForm10 = useForm<SASS>({
    defaultValues: SASS_TEMPLATE,
    mode: "onChange",
  });

  const assignmentForms = [
    assignmentForm,
    assignmentForm2,
    assignmentForm3,
    assignmentForm4,
    assignmentForm5,
    assignmentForm6,
    assignmentForm7,
    assignmentForm8,
    assignmentForm9,
    assignmentForm10,
  ];

  const [activeAssignmentCount, setActiveAssignmentCount] = useState(1);

  const classroomForm = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm2 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm3 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm4 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm5 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm6 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm7 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm8 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm9 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });
  const classroomForm10 = useForm<SCLR>({
    defaultValues: SCLR_TEMPLATE,
    mode: "onChange",
  });

  const classroomForms = [
    classroomForm,
    classroomForm2,
    classroomForm3,
    classroomForm4,
    classroomForm5,
    classroomForm6,
    classroomForm7,
    classroomForm8,
    classroomForm9,
    classroomForm10,
  ];

  const [activeClassroomCount, setActiveClassroomCount] = useState(1);

  const staffRecords: STFF[] = leaData?.stff ?? [];

  const canCreateNew = !!(
    searchCriteria?.firstName &&
    searchCriteria?.lastName &&
    searchCriteria?.dateOfBirth
  );

  const normalizeDateForComparison = (
    dateString: string | undefined
  ): string => {
    if (!dateString) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Pre-populate form helper (following child wizard pattern)
  const prePopulateForm = (
    targetForm: UseFormReturn<any>,
    includeLocalStaffId: boolean = true,
    includeProgramYear: boolean = true,
    includePreschoolCode: boolean = false
  ) => {
    const stffData = form.getValues();
    const currentData = targetForm.getValues();

    if (includeLocalStaffId && !currentData.LocalStaffId) {
      targetForm.setValue("LocalStaffId", stffData.LocalStaffId || "");
    }
    if (includeProgramYear && !currentData.ProgramYear) {
      targetForm.setValue("ProgramYear", stffData.ProgramYear || "");
    }
    if (includePreschoolCode && !currentData.PreschoolCode) {
      targetForm.setValue("PreschoolCode", "");
    }
  };

  const addAssignment = () => {
    if (activeAssignmentCount >= MAX_ASSIGNMENTS) return;
    const newIndex = activeAssignmentCount;
    const firstAssignment = assignmentForms[0].getValues();
    assignmentForms[newIndex].reset({
      ...SASS_TEMPLATE,
      ProgramYear: firstAssignment.ProgramYear || "",
      LocalStaffId: firstAssignment.LocalStaffId || "",
    });
    setActiveAssignmentCount((c) => c + 1);
  };

  const removeAssignment = (index: number) => {
    if (index <= 0 || index >= activeAssignmentCount) return;

    for (let i = index; i < activeAssignmentCount - 1; i++) {
      assignmentForms[i].reset(assignmentForms[i + 1].getValues());
    }
    assignmentForms[activeAssignmentCount - 1].reset(SASS_TEMPLATE);
    setActiveAssignmentCount((c) => c - 1);
  };

  const addClassroom = () => {
    if (activeClassroomCount >= MAX_CLASSROOMS) return;
    const newIndex = activeClassroomCount;
    const stffValues = form.getValues();
    classroomForms[newIndex].reset({
      ...SCLR_TEMPLATE,
      ProgramYear: stffValues.ProgramYear || "",
      LocalStaffId: stffValues.LocalStaffId || "",
    });
    setActiveClassroomCount((c) => c + 1);
  };

  const removeClassroom = (index: number) => {
    if (index <= 0 || index >= activeClassroomCount) return;

    for (let i = index; i < activeClassroomCount - 1; i++) {
      classroomForms[i].reset(classroomForms[i + 1].getValues());
    }
    classroomForms[activeClassroomCount - 1].reset(SCLR_TEMPLATE);
    setActiveClassroomCount((c) => c - 1);
  };

  const handleSearch = (filters: StaffSearchFormState) => {
    const name = [filters.firstName, filters.lastName]
      .filter(Boolean)
      .join(" ");

    setSearchedData({
      name: name,
    });
    setSearchCriteria(filters);

    const staffLocalId = filters.staffLocalId?.trim().toLowerCase();
    const first = filters.firstName?.trim().toLowerCase();
    const last = filters.lastName?.trim().toLowerCase();
    const dob = filters.dateOfBirth?.trim();

    const isStaffLocalIdSearch = !!staffLocalId;

    const results = staffRecords.filter((staff) => {
      if (staffLocalId && staff.LocalStaffId?.toLowerCase() !== staffLocalId) {
        return false;
      }

      if (first && !staff.StaffLegalFirstName?.toLowerCase().includes(first)) {
        return false;
      }

      if (last && !staff.StaffLegalLastName?.toLowerCase().includes(last)) {
        return false;
      }

      if (dob) {
        const normalizedSearchDob = normalizeDateForComparison(dob);
        const normalizedStaffDob = normalizeDateForComparison(
          staff.StaffDateOfBirth
        );

        if (normalizedSearchDob && normalizedStaffDob) {
          if (normalizedSearchDob !== normalizedStaffDob) {
            return false;
          }
        }
      }

      return true;
    });

    setSearchResults(results);

    if (isStaffLocalIdSearch) {
      if (results.length === 0) {
        setSearchError(
          `No matches found for Staff Local ID "${filters.staffLocalId}". Please search again using First Name and Last Name.`
        );
        setShowResults(false);
        setSearchCriteria({
          staffLocalId: "",
          firstName: "",
          lastName: "",
          dateOfBirth: "",
        });
      } else if (results.length === 1) {
        setSearchError("");
        handleSelectStaff(results[0]);
      } else {
        setSearchError("");
        setShowResults(true);
      }
    } else {
      setSearchError("");
      setShowResults(true);
    }
  };

  const handleSelectStaff = (staff: STFF) => {
    setIsNewRecord(false);
    setLockedFields({
      staffLocalId: true,
      firstName: false,
      lastName: false,
      dateOfBirth: false,
    });

    const normalizedStaff: STFF = {
      ...staff,
      StaffEthnicity: convertEthnicity(
        staff.StaffEthnicity,
        staff.StaffEthnicityMissingIndicator
      ),
      StaffEthnicityMissingIndicator: convertMissingIndicator(
        staff.StaffEthnicityMissingIndicator
      ),
      StaffRaceMissingIndicator: convertMissingIndicator(
        staff.StaffRaceMissingIndicator
      ),
      StaffRace1: staff.StaffRace1 ?? "",
      StaffRace2: staff.StaffRace2 ?? "",
      StaffRace3: staff.StaffRace3 ?? "",
      StaffRace4: staff.StaffRace4 ?? "",
      StaffRace5: staff.StaffRace5 ?? "",
    };

    form.reset(normalizedStaff);
    setCurrentStep(2);

    setCompletedSteps((prev) => {
      const newCompleted = new Set([...prev, 2]);
      return Array.from(newCompleted).sort((a, b) => a - b);
    });
  };

  const handleCreateNew = () => {
    if (
      !searchCriteria?.firstName ||
      !searchCriteria?.lastName ||
      !searchCriteria?.dateOfBirth
    ) {
      return;
    }

    setIsNewRecord(true);
    const newStaffData = {
      ...STFF_TEMPLATE,
      StaffLegalFirstName: searchCriteria.firstName,
      StaffLegalLastName: searchCriteria.lastName,
      StaffDateOfBirth: searchCriteria.dateOfBirth,
      LocalStaffId: "",
      StaffEthnicity: UNSET_ETHNICITY,
    };
    form.reset(newStaffData);
    setLockedFields({
      staffLocalId: false,
      firstName: true,
      lastName: true,
      dateOfBirth: true,
    });
    setCurrentStep(2);

    setCompletedSteps((prev) => {
      const newCompleted = new Set([...prev, 2]);
      return Array.from(newCompleted).sort((a, b) => a - b);
    });
  };

  const resetWizard = () => {
    setCurrentStep(1);
    setCompletedSteps([1]);
    setSearchResults([]);
    setShowResults(false);
    setSearchedData({ name: "" });
    setSearchCriteria(null);
    setIsNewRecord(false);
    setLockedFields({
      staffLocalId: false,
      firstName: false,
      lastName: false,
      dateOfBirth: false,
    });
    setSearchError("");
    setSkipAssignment(false);
    setSkipClassroom(false);
    form.reset({
      ...STFF_TEMPLATE,
      StaffEthnicity: UNSET_ETHNICITY,
    });
    assignmentForms.forEach((f) => f.reset(SASS_TEMPLATE));
    setActiveAssignmentCount(1);
    classroomForms.forEach((f) => f.reset(SCLR_TEMPLATE));
    setActiveClassroomCount(1);
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setSearchError("");
      setCurrentStep(1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const clearSearchError = () => {
    setSearchError("");
  };

  const handleSkipAssignment = (skip: boolean) => {
    setSkipAssignment(skip);
    if (skip) {
      const stffValues = form.getValues();
      assignmentForms.forEach((f) =>
        f.reset({
          ...SASS_TEMPLATE,
          LocalStaffId: stffValues.LocalStaffId || "",
          ProgramYear: stffValues.ProgramYear || "",
          PreschoolCode: "",
        })
      );
      setActiveAssignmentCount(1);
    } else {
      prePopulateForm(assignmentForms[0], true, true, false);
    }
  };

  const handleSkipClassroom = (skip: boolean) => {
    setSkipClassroom(skip);
    if (skip) {
      const stffValues = form.getValues();
      classroomForms.forEach((f) =>
        f.reset({
          ...SCLR_TEMPLATE,
          LocalStaffId: stffValues.LocalStaffId || "",
          ProgramYear: stffValues.ProgramYear || "",
          PreschoolCode: "",
        })
      );
      setActiveClassroomCount(1);
    } else {
      prePopulateForm(classroomForms[0], true, true, false);
    }
  };

  return {
    currentStep,
    completedSteps,
    searchResults,
    showResults,
    searchedData,
    isNewRecord,
    lockedFields,
    form,
    assignmentForms,
    activeAssignmentCount,
    classroomForms,
    activeClassroomCount,
    canCreateNew,
    searchError,
    searchCriteria,
    skipAssignment,
    skipClassroom,
    handleSearch,
    handleSelectStaff,
    handleCreateNew,
    handleBack,
    resetWizard,
    setCurrentStep,
    setCompletedSteps,
    setSearchError,
    clearSearchError,
    handleSkipAssignment,
    handleSkipClassroom,
    addAssignment,
    removeAssignment,
    addClassroom,
    removeClassroom,
  };
}
